import React, { useState, useEffect, useCallback } from 'react';
import { ShareIcon, XMarkIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Dialog from '../shared/Dialog';
import Button from '../shared/Button';
import { showToast } from '../shared/Toast';
import folderDocumentShareService from '../services/FolderDocumentShareService';
import defaultProjectPlaceHolder from '../../assets/images/project_placeholder@2x.jpg';

// Simplified User interface based on Directory.tsx
interface User {
  id: number;
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  croppedAvatarUrl: string;
  role?: string;
}

interface Props {
  className?: string;
  projectId: string;
  projectFolderId?: string | number;
  documentId?: string | number;
  itemName: string;
  currentUserRole?: string;
}

// const DEFAULT_AVATAR = '/assets/images/project_placeholder@2x.jpg'; // Adjust path if needed

export default function FolderDocumentShareDropdownItem({
  className,
  projectId,
  projectFolderId,
  documentId,
  itemName,
  currentUserRole: propRole,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [externalEmails, setExternalEmails] = useState<string[]>(['']);
  const [isSharing, setIsSharing] = useState(false);
  
  const itemType = documentId ? 'File' : 'Folder';
  const itemTypeLower = itemType.toLowerCase();
  
  // New States for Member Picker
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserUuids, setSelectedUserUuids] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  const [currentlySharedWith, setCurrentlySharedWith] = useState<any[]>([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(propRole || null);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(!propRole);

  const resetForm = () => {
    setExternalEmails(['']);
    setSelectedUserUuids(new Set());
    setSearchTerm('');
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = defaultProjectPlaceHolder;
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
      setCurrentlySharedWith([]);
      loadAvailableUsers();
      loadExistingShares();
    }
  }, [isOpen, projectFolderId, documentId]);

  useEffect(() => {
    if (!propRole) {
      checkPermissions();
    } else {
      setCurrentUserRole(propRole);
      setIsCheckingPermissions(false);
    }
  }, [projectId, propRole]);

  const checkPermissions = async () => {
    try {
      const response = await fetch(`/projects/${projectId}/participants`, {
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      
      // Get current user UUID from meta tags or a known global
      const currentUserUuid = document.querySelector('meta[name="current-user-uuid"]')?.getAttribute('content');
      
      const sessionResponse = await fetch('/auth/current', { headers: { 'Accept': 'application/json' } });
      const sessionData = await sessionResponse.json();
      
      // If we can't find the user by UUID in participants directly, we'll need to find them by email if the session provides it
      // Standard approach in this app seems to be finding the project user record
      const me = data.users.find((u: any) => u.email === sessionData.email);
      if (me) {
        setCurrentUserRole(me.role_key || me.role); // participants might return humanized or key
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
    } finally {
      setIsCheckingPermissions(false);
    }
  };

  const loadAvailableUsers = async (keyword: string = '') => {
    setIsLoadingUsers(true);
    try {
      const response = await folderDocumentShareService.fetchAvailableUsers(keyword, projectId);
      setAvailableUsers(response.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadExistingShares = async () => {
    setCurrentlySharedWith([]);
    setIsLoadingExisting(true);
    try {
      const response = await folderDocumentShareService.fetchExistingShares(projectId, {
        project_folder_id: projectFolderId,
        document_id: documentId
      });
      setCurrentlySharedWith(response.shared_with || []);
    } catch (error) {
      console.error('Error loading existing shares:', error);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Debounce search could be added here
    loadAvailableUsers(value);
  };

  const toggleUserSelection = (uuid: string) => {
    const next = new Set(selectedUserUuids);
    if (next.has(uuid)) {
      next.delete(uuid);
    } else {
      next.add(uuid);
    }
    setSelectedUserUuids(next);
  };

  const handleAddEmail = () => {
    setExternalEmails([...externalEmails, '']);
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...externalEmails];
    newEmails[index] = value;
    // const uuid = availableUsers.find(u => u.email === value)?.uuid || ''
    // if (uuid && uuid != '') {
    //   toggleUserSelection(uuid)
    // }
    setExternalEmails(newEmails);
  };

  const handleRemoveEmail = (index: number) => {
    const newEmails = externalEmails.filter((_, i) => i !== index);
    setExternalEmails(newEmails.length ? newEmails : ['']);
  };

  const handleShare = async () => {
    const validEmails = externalEmails.filter(email => email.trim() !== '' && email.includes('@'));
    const selectedUsers = Array.from(selectedUserUuids);

    if (validEmails.length === 0 && selectedUsers.length === 0) {
      showToast('Please select at least one user or enter a valid email address.', 'danger');
      return;
    }

    setIsSharing(true);
    try {
      const emailRecipients = validEmails.map(email => ({ recipient_email: email }));
      const userRecipients = selectedUsers.map(uuid => {
        const user = availableUsers.find(u => u.uuid === uuid);
        return { user_id: user?.id };
      });

      const recipients = [...emailRecipients, ...userRecipients];

      await folderDocumentShareService.shareItem(projectId, {
        project_folder_id: projectFolderId,
        document_id: documentId,
        recipients,
      });
      
      showToast(`${itemType} invitations sent successfully!`, 'success');
      setIsOpen(false);
      resetForm();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const duplicateErrors = error.response.data.errors
          .flatMap((err: any) => err.errors)
          .filter((msg: string) => msg.includes('already shared'));
        
        if (duplicateErrors.length > 0) {
          duplicateErrors.forEach((msg: string) => showToast(msg, 'danger'));
        } else {
          showToast(`Failed to share ${itemTypeLower}. Please try again.`, 'danger');
        }
      } else {
        showToast(`Failed to share ${itemTypeLower}. Please try again.`, 'danger');
        console.error(error);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevoke = async (shareId: number) => {
    try {
      const resp = await folderDocumentShareService.revokeShare(projectId, shareId);
      if (resp.success) {
        showToast(`${itemType} access revoked`, 'success');
        setCurrentlySharedWith(prev => prev.filter(share => share.id !== shareId));
      } else {
        showToast(resp.error || 'Could not revoke access', 'danger');
      }
    } catch (e) {
      showToast('Error revoking access', 'danger');
    }
  };

  // Permission check: SC, CA, SOC only
  const ALLOWED_ROLES = ['super_coordinator', 'coordinator_admin', 'stunt_office_coordinator'];
  const canShare = currentUserRole && ALLOWED_ROLES.includes(currentUserRole.toLowerCase().replace(/\s+/g, '_'));

  if (isCheckingPermissions) return null;
  if (!canShare) return null;

  return (
    <>
      <div className={className} onClick={() => {
          setIsOpen(true);
          document.querySelectorAll('[data-controller~="dropdown"]').forEach((el) => {
              const menu = el.querySelector('[data-dropdown-target="menu"]');
              if (menu && !menu.classList.contains('hidden')) {
                menu.classList.add('hidden');
              }
          });
          document.querySelectorAll('[data-modal-target="container"]').forEach((el) => {
              if (el && !el.classList.contains('hidden')) {
                el.classList.add('hidden');
                el.classList.add('pointer-events-none');
              }
          });
        }}>
        <ShareIcon className="h-5 w-5 mr-3 lg:mr-4 text-gray-500" />
        Share Access
      </div>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="max-w-4xl w-full h-full sm:h-auto ">
        <div className="bg-white rounded-xl overflow-hidden flex flex-col h-full" style={{ maxHeight: '70vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Share Access</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{itemName}</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 p-1 ml-2"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
              
              {/* Left Column: Internal Users */}
              <div className="flex flex-col border-b lg:border-b-0 lg:border-r lg:pr-6 border-gray-200" style={{ minHeight: '18.75rem' }}>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Share with Team Members</h3>
                
                {/* Search box */}
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Search users..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* User List */}
                <div className="flex-1 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3" style={{ minHeight: '12.5rem', maxHeight: '14rem' }}>
                  {isLoadingUsers ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : availableUsers.length > 0 ? (
                    availableUsers
                      .filter(user => !currentlySharedWith.some(shared => shared.is_internal && shared.uuid === user.uuid))
                      .map(user => (
                      <div
                        key={user.uuid}
                        onClick={() => toggleUserSelection(user.uuid)}
                        className={`flex items-center justify-between p-2 truncate border rounded-lg cursor-pointer transition-colors ${
                          selectedUserUuids.has(user.uuid) ? 'bg-blue-50 border-blue-300' : 'border-transparent hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            src={user.avatarUrl=='icons/default_avatar.svg'?defaultProjectPlaceHolder:user.croppedAvatarUrl || user.avatarUrl || defaultProjectPlaceHolder}
                            className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                            alt={user.firstName}
                            onError={handleImageError}
                          />
                          <div className="truncate">
                            <p className="text-sm font-medium text-gray-900 truncate">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-gray-500 truncate">{user.role || 'Member'}</p>
                          </div>
                        </div>
                        {selectedUserUuids.has(user.uuid) && (
                          <span className="text-blue-500 font-bold ml-2">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 text-sm py-10">No users found</p>
                  )}
                </div>
              </div>

              {/* Right Column: Currently Shared & External Invites */}
              <div className="flex flex-col" style={{ minHeight: '18.75rem' }}>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Currently Shared With ({currentlySharedWith.length})</h3>
                
                <div className="flex-1 overflow-y-auto space-y-2 mb-6 border border-gray-100 rounded-lg p-2" style={{ maxHeight: '14rem' }}>
                   {isLoadingExisting ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                      </div>
                   ) : currentlySharedWith.length > 0 ? (
                      currentlySharedWith.map(share => (
                        <div key={share.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 min-w-0">
                             {share.is_internal ? (
                                <>
                                  
                                  <img 
                                  // src={share.croppedAvatarUrl || defaultProjectPlaceHolder} 
                                  src={share.avatarUrl=='icons/default_avatar.svg'?defaultProjectPlaceHolder:share.croppedAvatarUrl || share.avatarUrl || defaultProjectPlaceHolder}
                                  className="w-10 h-10 rounded-full flex-shrink-0 object-cover" />
                                  <span className="text-sm truncate text-gray-700">{share.firstName} {share.lastName}</span>
                                </>
                             ) : (
                                <span className="text-sm truncate text-gray-700">{share.recipient_email}</span>
                             )}
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRevoke(share.id);
                            }}
                            className="text-red-500 hover:text-red-700 font-semibold uppercase tracking-wider transition-colors ml-2 py-1 px-2 border border-red-200 hover:bg-red-50 rounded" 
                            style={{ fontSize: '0.625rem' }}
                          >
                            Revoke
                          </button>
                        </div>
                      ))
                   ) : (
                      <p className="text-xs text-center text-gray-400 py-4">Not shared with anyone yet</p>
                   )}
                </div>

                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Share with External Link</h3>
                
                <p className="text-xs sm:text-sm text-gray-500 mb-4">
                  Add non-members by email. They will receive a unique link and verification code.
                </p>

                <div className="space-y-3 mb-4">
                  {externalEmails.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                        placeholder="name@example.com"
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2 border"
                      />
                      {externalEmails.length > 1 && (
                        <button
                          onClick={() => handleRemoveEmail(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={handleAddEmail}
                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add another email
                  </button>
                </div>

                {/* Summary / Selected List */}
                {(selectedUserUuids.size > 0 || externalEmails.some(e => e.trim() !== '')) && (
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mt-auto">
                    <p className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-2">Selection Summary</p>
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                      {Array.from(selectedUserUuids).map(uuid => {
                        const user = availableUsers.find(u => u.uuid === uuid);
                        return (
                          <span key={uuid} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700">
                             {user ? `${user.firstName} ${user.lastName}` : 'User'}
                             <XMarkIcon className="w-3 h-3 cursor-pointer" onClick={() => toggleUserSelection(uuid)} />
                          </span>
                        );
                      })}
                      {externalEmails.filter(e => e.trim() !== '').map((email, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700">
                          {email}
                          <XMarkIcon className="w-3 h-3 cursor-pointer" onClick={() => handleRemoveEmail(idx)} />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50 gap-4">
            <p className="text-xs text-gray-500 max-w-sm text-center sm:text-left">
              Internal users will see this in their "General Documents" folder. External recipients will receive an OTP via email.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
               <button
                onClick={() => setIsOpen(false)}
                className="order-2 sm:order-1 px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={isSharing || (selectedUserUuids.size === 0 && externalEmails.every(e => e.trim() === ''))}
                className="order-1 sm:order-2 px-8 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSharing ? 'Sending...' : 'Share Access'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
